import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  AlertCircle,
  AlertTriangle,
  CalendarCheck,
  CheckCircle,
  Info,
  Lock,
  Search,
  Truck,
  X,
  XCircle,
  Zap,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FEDispatchMap } from '@/components/forms/parentTicket/FEDispatchMap'
import { isActivityWindowValid, timeToMinutes } from '@/components/forms/parentTicket/feDispatchUtils'
import type { EngineerConflictResult } from '@/components/forms/parentTicket/FEScheduleConflictBanner'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { getEngineerAssignmentConflict } from '@/components/forms/parentTicket/feDispatchPersistence'
import { additionalFeOrdinalLabel } from '@/components/forms/parentTicket/feDispatchRole'
import type { FESchedule } from '@/types/parentTicket'
import type { FieldEngineerRecommendation } from '@/types/ticket'

export type AdditionalSlot = { id: number | null; name?: string | null }

const MANUAL_LIST_PAGE_SIZE = 10

export interface FERecommenderPanelProps {
  recommendations: FieldEngineerRecommendation[]
  loading?: boolean
  /** True while primary assignment (auto or manual) is in flight. */
  assigningPrimary?: boolean
  /** Backup slot index (0-based) currently being assigned, if any. */
  assigningAdditionalSlotIndex?: number | null
  isReadOnly?: boolean
  disableAutoAssignWhenAssigned?: boolean
  primaryEngineerId?: number | null
  primaryEngineerName?: string | null
  additionalSlots?: AdditionalSlot[]
  referenceType?: 'store' | 'location' | null
  referenceName?: string | null
  referenceCode?: string | null
  storeName?: string | null
  storeCode?: string | null
  storeLatitude?: number | null
  storeLongitude?: number | null
  areaType?: string | null
  hardwareType?: string | null
  storeSearchSlot?: React.ReactNode
  onEditStore?: () => void
  /**
   * When false, hide the FE overview map (e.g. while the site pin is still being
   * placed/confirmed so the TL only sees one map).
   */
  showOverviewMap?: boolean
  ticketId?: string | null
  preferredScheduleAt?: string | null
  certificationLabel?: string | null
  requiredCertifications?: string[]
  activityScheduleFrom?: string
  activityScheduleTo?: string
  supportDurationOptions?: Array<{ code: string; label: string }>
  initialManualEndOverride?: boolean
  initialManualEndOverrideReason?: string
  onActivityScheduleChange?: (fromLocal: string, toLocal: string, overrideReason?: string) => void
  onActivityScheduleApply?: (fromLocal: string, toLocal: string) => void
  feAvailabilityByUser?: Record<number, FESchedule[]>
  scheduleDateStr?: string | null
  onSelectPrimary: (rec: FieldEngineerRecommendation) => void
  onSelectAdditional: (rec: FieldEngineerRecommendation, slotIndex: number) => void
  onClearPrimary: () => void | boolean | Promise<void | boolean>
  onClearAdditional: (slotIndex: number) => void | boolean | Promise<void | boolean>
  onAddAdditionalSlot?: () => void
  onScheduleChange: (iso: string) => void
  onPromoteAdditional: () => void
  onFindNewPrimary: () => void
  onKeepAnyway?: () => void
  onRefresh?: () => void
  /** Fired when manual primary/backup selection opens (expand ranked list). */
  onManualModeOpen?: () => void
  wantsAdditional?: boolean
  onWantsAdditionalChange?: (checked: boolean) => void
  /** Increment after a failed primary persist to stop auto-assign retry loops. */
  assignFailedSignal?: number
}

type PanelState =
  | 'auto'
  | 'with-additional'
  | 'change-primary'
  | 'change-additional'
  | 'editing'
  | 'conflict'
  | 'resolved-swap'
  | 'resolved-new'
  | 'resolved-keep'

type ScheduleBannerState = 'normal' | 'editing' | 'checking' | 'hidden'

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function formatDistance(km: number | null | undefined, source?: string | null): string {
  if (km == null || !Number.isFinite(km)) return '—'
  const rounded = Math.round(Number(km) * 10) / 10
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
  if (source === 'osrm') return `${label} km road`
  if (source === 'haversine') return `${label} km approx`
  return `${label} km`
}

function formatTravelMinutes(travel: number | null | undefined): string {
  if (travel == null || !Number.isFinite(Number(travel)) || Number(travel) <= 0) return '—'
  const minutes = Math.round(Number(travel))
  if (minutes < 60) return `${minutes} min travel`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h}h ${m}m travel` : `${h}h travel`
}

function formatAptMinutes(travel: number | null | undefined, repair: number | null | undefined): string {
  const travelPart = travel != null && Number.isFinite(Number(travel)) ? Number(travel) : 0
  const repairPart = repair != null && Number.isFinite(Number(repair)) ? Number(repair) : 0
  const total = travelPart + repairPart
  if (total <= 0) return '—'
  const h = Math.floor(total / 60)
  const m = total % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

function formatPrimaryMetaLine(rec: FieldEngineerRecommendation): string {
  const place = rec.cluster ?? rec.launch_point ?? null
  const distance = formatDistance(rec.distance_km, rec.distance_source)
  const travel = formatTravelMinutes(rec.apt_travel_minutes)
  const parts = [place, distance, travel].filter((part) => part && part !== '—')
  if (parts.length === 0) {
    return [distance, travel].join(' · ')
  }
  return parts.join(' · ')
}

function formatActivityLabel(fromLocal?: string, toLocal?: string): string {
  if (!fromLocal || !toLocal) return 'Not set'
  const from = new Date(fromLocal)
  const to = new Date(toLocal)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Not set'
  const fmt = (d: Date) =>
    d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  return `${fmt(from)} → ${fmt(to)}`
}

function buildTimelineSegments(
  schedules: FESchedule[] | undefined,
  scheduleDateStr: string | null | undefined,
  preferredScheduleAt: string | null | undefined,
  aptMinutes: number,
): number[] {
  const segments = Array(20).fill(0)
  const dayStr = scheduleDateStr ?? ''

  for (const s of schedules ?? []) {
    if (dayStr && !s.schedule_date?.startsWith(dayStr)) continue
    const startM = timeToMinutes(s.schedule_time)
    const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
    for (let i = 0; i < 20; i++) {
      const segStart = 480 + i * 30
      const segEnd = segStart + 30
      if (segStart < endM && segEnd > startM) segments[i] = 1
    }
  }

  if (preferredScheduleAt) {
    const d = new Date(preferredScheduleAt)
    const datePart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    if (!dayStr || datePart === dayStr) {
      const reqStart = d.getHours() * 60 + d.getMinutes()
      const reqEnd = reqStart + aptMinutes
      for (let i = 0; i < 20; i++) {
        const segStart = 480 + i * 30
        const segEnd = segStart + 30
        if (segStart < reqEnd && segEnd > reqStart && segments[i] === 0) segments[i] = 2
      }
    }
  }

  return segments
}

function timelineSegmentColor(v: number): string {
  if (v === 0) return '#1baf7a'
  if (v === 2) return '#205BF3'
  return '#CBD5E1'
}

function scoreBarColor(score: number): string {
  if (score > 80) return '#1baf7a'
  if (score > 60) return '#205BF3'
  return '#eda100'
}

function findRecByUserId(
  recommendations: FieldEngineerRecommendation[],
  userId: number | null | undefined,
): FieldEngineerRecommendation | null {
  if (userId == null) return null
  const target = Number(userId)
  if (!Number.isFinite(target)) return null
  return recommendations.find((r) => Number(r.user_id) === target) ?? null
}

function Tag({
  children,
  variant = 'neutral',
}: {
  children: React.ReactNode
  variant?: 'green' | 'amber' | 'red' | 'blue' | 'neutral' | 'lock'
}) {
  const styles = {
    green: { bg: 'green.50', color: 'green.700' },
    amber: { bg: 'orange.50', color: 'orange.700' },
    red: { bg: 'red.50', color: 'red.600' },
    blue: { bg: 'blue.50', color: 'blue.700' },
    neutral: { bg: 'gray.50', color: 'gray.600', border: '0.5px solid', borderColor: 'gray.200' },
    lock: { bg: 'orange.50', color: 'orange.700', border: '0.5px solid', borderColor: 'orange.200' },
  }[variant]

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="3px"
      fontSize="9px"
      fontWeight="500"
      px="6px"
      py="2px"
      borderRadius="full"
      whiteSpace="nowrap"
      {...styles}
    >
      {children}
    </Box>
  )
}

function SlotPreloader({
  label,
  message,
  accent,
}: {
  label: string
  message: string
  accent: 'blue' | 'green'
}) {
  const border = accent === 'blue' ? 'blue.300' : 'green.300'
  const titleColor = accent === 'blue' ? 'blue.700' : 'green.700'
  const spinnerColor = accent === 'blue' ? 'blue.500' : 'green.500'
  return (
    <Box
      border="0.5px dashed"
      borderColor={border}
      borderRadius="md"
      p={2.5}
      mb={accent === 'blue' ? 2 : 0}
      mt={accent === 'green' ? 1.5 : 0}
      bg="transparent"
    >
      <Text fontSize="10px" fontWeight="500" color={titleColor} mb={1.5}>
        {label}
      </Text>
      <HStack spacing={2}>
        <Spinner size="sm" color={spinnerColor} thickness="2px" />
        <Text fontSize="11px" color="gray.500">
          {message}
        </Text>
      </HStack>
    </Box>
  )
}

export function FERecommenderPanel({
  recommendations,
  loading,
  assigningPrimary = false,
  assigningAdditionalSlotIndex = null,
  isReadOnly,
  disableAutoAssignWhenAssigned,
  primaryEngineerId,
  primaryEngineerName,
  additionalSlots: additionalSlotsProp,
  referenceType,
  referenceName,
  referenceCode,
  storeName,
  storeCode,
  storeLatitude,
  storeLongitude,
  areaType,
  hardwareType,
  storeSearchSlot,
  onEditStore,
  showOverviewMap = true,
  ticketId,
  preferredScheduleAt,
  certificationLabel,
  requiredCertifications,
  activityScheduleFrom,
  activityScheduleTo,
  onActivityScheduleChange,
  onActivityScheduleApply,
  supportDurationOptions,
  initialManualEndOverride = false,
  initialManualEndOverrideReason = '',
  feAvailabilityByUser,
  scheduleDateStr,
  onSelectPrimary,
  onSelectAdditional,
  onClearPrimary,
  onClearAdditional,
  onAddAdditionalSlot,
  onPromoteAdditional,
  onFindNewPrimary,
  onKeepAnyway,
  onRefresh,
  onManualModeOpen,
  wantsAdditional: controlledWantsAdditional,
  onWantsAdditionalChange,
  assignFailedSignal = 0,
}: FERecommenderPanelProps) {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const autoAssignedRef = useRef(false)
  const autoAdditionalRef = useRef(false)
  const checkRequestRef = useRef(0)
  const skipAutoCheckRef = useRef(false)
  const suppressAutoAssignRef = useRef(false)

  useEffect(() => {
    if (assignFailedSignal > 0) {
      // Persist failed and primaryEngineerId is still null. Keep guards set so
      // auto-assign does not immediately re-fire the same engineer in a loop.
      // User can still pick manually (manual select sets these guards itself).
      autoAssignedRef.current = true
      suppressAutoAssignRef.current = true
    }
  }, [assignFailedSignal])

  const additionalSlots = useMemo(() => additionalSlotsProp ?? [], [additionalSlotsProp])
  const additionalIds = useMemo(
    () => additionalSlots.map((slot) => slot.id).filter((id): id is number => id != null),
    [additionalSlots],
  )
  const firstAdditionalId = additionalIds[0] ?? null

  const [panelState, setPanelState] = useState<PanelState>('auto')
  const panelStateRef = useRef<PanelState>('auto')
  const [editingAdditionalIndex, setEditingAdditionalIndex] = useState<number | null>(null)
  const [wantsAdditionalState, setWantsAdditionalState] = useState(additionalSlots.length > 0)
  const wantsAdditional =
    controlledWantsAdditional !== undefined ? controlledWantsAdditional : wantsAdditionalState

  const setWantsAdditional = useCallback(
    (checked: boolean) => {
      if (controlledWantsAdditional === undefined) {
        setWantsAdditionalState(checked)
      }
      onWantsAdditionalChange?.(checked)
    },
    [controlledWantsAdditional, onWantsAdditionalChange],
  )

  const [scheduleBannerState, setScheduleBannerState] = useState<ScheduleBannerState>('hidden')
  const [conflicts, setConflicts] = useState<EngineerConflictResult | null>(null)
  const [manualSearchQuery, setManualSearchQuery] = useState('')
  const [searchOtherClusters, setSearchOtherClusters] = useState(false)
  const [debouncedManualSearch, setDebouncedManualSearch] = useState('')
  const [searchResults, setSearchResults] = useState<FieldEngineerRecommendation[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [manualListVisibleCount, setManualListVisibleCount] = useState(MANUAL_LIST_PAGE_SIZE)
  const [clearingAssignment, setClearingAssignment] = useState(false)
  const prevManualModeRef = useRef<'primary' | 'additional' | null>(null)
  const autoEnabledClusterSearchRef = useRef(false)

  const primaryRec = useMemo(
    () => findRecByUserId(recommendations, primaryEngineerId),
    [recommendations, primaryEngineerId],
  )

  const findNextEligible = useCallback(
    (extraExclude?: number | null): FieldEngineerRecommendation | null => {
      const exclude = new Set<number>()
      if (primaryEngineerId != null) exclude.add(primaryEngineerId)
      for (const id of additionalIds) exclude.add(id)
      if (extraExclude != null) exclude.add(extraExclude)
      return (
        recommendations.find(
          (r) =>
            !r.is_locked &&
            r.user_id != null &&
            Number(r.user_id) > 0 &&
            !exclude.has(r.user_id),
        ) ?? null
      )
    },
    [recommendations, primaryEngineerId, additionalIds],
  )

  const hasCertRequirement = Boolean(
    certificationLabel || (requiredCertifications && requiredCertifications.length > 0),
  )
  const certLabel = certificationLabel ?? requiredCertifications?.[0] ?? null

  const displayReferenceName = referenceName ?? storeName
  const displayReferenceCode = referenceType === 'location' ? null : (referenceCode ?? storeCode)
  const displayReferencePrefix = referenceType === 'location' ? 'Location' : 'Store'

  const canCheckConflicts =
    Boolean(ticketId) &&
    Boolean(preferredScheduleAt) &&
    (primaryEngineerId != null || firstAdditionalId != null)

  const runConflictCheck = useCallback(
    async (scheduleIso: string) => {
      if (!ticketId || !scheduleIso) return

      const requestId = ++checkRequestRef.current
      setScheduleBannerState('checking')

      const res = await TicketService.checkEngineerConflicts(ticketId, {
        primary_engineer_id: primaryEngineerId ?? undefined,
        additional_fe_user_id: firstAdditionalId ?? undefined,
        schedule: scheduleIso,
      })

      if (requestId !== checkRequestRef.current) return
      const inManualSelection =
        panelStateRef.current === 'change-primary' ||
        panelStateRef.current === 'change-additional'

      if (!res.success || !res.data) {
        setConflicts(null)
        setScheduleBannerState('normal')
        if (!inManualSelection) {
          setPanelState(wantsAdditional || firstAdditionalId ? 'with-additional' : 'auto')
        }
        return
      }

      setConflicts(res.data)
      const additionalAvailable = (res.data.additional ?? res.data.backup)?.available !== false
      const hasConflict = !res.data.primary.available || !additionalAvailable
      if (hasConflict) {
        if (!inManualSelection) setPanelState('conflict')
        setScheduleBannerState('normal')
      } else {
        setScheduleBannerState('normal')
        if (!inManualSelection) {
          setPanelState(wantsAdditional || firstAdditionalId ? 'with-additional' : 'auto')
        }
      }
    },
    [ticketId, primaryEngineerId, firstAdditionalId, wantsAdditional],
  )

  useEffect(() => {
    panelStateRef.current = panelState
  }, [panelState])

  useEffect(() => {
    // When controlled, parent owns the checkbox — don't force re-check after user opts out.
    if (controlledWantsAdditional !== undefined) return
    if (additionalIds.length > 0) {
      setWantsAdditionalState(true)
    }
  }, [additionalIds.length, controlledWantsAdditional])

  useEffect(() => {
    if (!canCheckConflicts || isReadOnly) {
      setScheduleBannerState('hidden')
      return
    }
    if (skipAutoCheckRef.current) return
    if (
      panelState === 'editing' ||
      panelState === 'change-primary' ||
      panelState === 'change-additional'
    ) {
      return
    }

    setScheduleBannerState('normal')
    void runConflictCheck(preferredScheduleAt!)
  }, [canCheckConflicts, isReadOnly, preferredScheduleAt, primaryEngineerId, firstAdditionalId, runConflictCheck, panelState])

  // Auto-assign primary
  useEffect(() => {
    if (
      isReadOnly ||
      disableAutoAssignWhenAssigned ||
      assigningPrimary ||
      primaryEngineerId ||
      recommendations.length === 0 ||
      autoAssignedRef.current ||
      suppressAutoAssignRef.current ||
      panelState === 'change-primary' ||
      panelState === 'change-additional'
    ) {
      return
    }
    const first = recommendations.find((r) => !r.is_locked && r.user_id != null && Number(r.user_id) > 0)
    if (first) {
      autoAssignedRef.current = true
      onSelectPrimary(first)
    }
  }, [
    isReadOnly,
    disableAutoAssignWhenAssigned,
    assigningPrimary,
    primaryEngineerId,
    recommendations,
    onSelectPrimary,
    panelState,
  ])

  useEffect(() => {
    if (panelStateRef.current !== 'change-primary' || !primaryEngineerId) {
      return
    }
    const nextState = wantsAdditional || additionalIds.length > 0 ? 'with-additional' : 'auto'
    panelStateRef.current = nextState
    setPanelState(nextState)
  }, [primaryEngineerId, wantsAdditional, additionalIds.length])

  // Auto-suggest the first backup slot when the user opts in
  useEffect(() => {
    if (
      isReadOnly ||
      !wantsAdditional ||
      !primaryEngineerId ||
      additionalSlots.length > 0 ||
      recommendations.length === 0 ||
      autoAdditionalRef.current ||
      panelState === 'change-primary' ||
      panelState === 'change-additional'
    ) {
      return
    }
    const next = findNextEligible()
    if (next) {
      autoAdditionalRef.current = true
      onSelectAdditional(next, 0)
    }
  }, [isReadOnly, wantsAdditional, primaryEngineerId, additionalSlots.length, recommendations, findNextEligible, onSelectAdditional, panelState])

  const manualMode: 'primary' | 'additional' | null =
    panelState === 'change-primary'
      ? 'primary'
      : panelState === 'change-additional'
        ? 'additional'
        : null

  const effectiveManualMode: 'primary' | 'additional' | null =
    manualMode !== null
      ? manualMode
      : !primaryEngineerId && !isReadOnly
        ? 'primary'
        : null

  const isManualSelecting = effectiveManualMode != null
  const isManualListBusy =
    Boolean(loading) ||
    searchLoading ||
    assigningPrimary ||
    assigningAdditionalSlotIndex != null ||
    clearingAssignment
  const manualListBusyMessage = searchLoading
    ? 'Searching field engineers…'
    : clearingAssignment
      ? 'Clearing assignment…'
      : assigningPrimary || assigningAdditionalSlotIndex != null
      ? 'Updating assignment…'
      : 'Loading field engineers…'

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedManualSearch(manualSearchQuery.trim())
    }, 300)
    return () => window.clearTimeout(timer)
  }, [manualSearchQuery])

  useEffect(() => {
    if (!effectiveManualMode) {
      setManualSearchQuery('')
      setDebouncedManualSearch('')
      setSearchResults([])
      setSearchLoading(false)
      setSearchOtherClusters(false)
      autoEnabledClusterSearchRef.current = false
      return
    }
    if (!searchOtherClusters) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }
    let cancelled = false
    setSearchLoading(true)
    void TicketService.searchFieldEngineers(debouncedManualSearch, {
      perPage: 50,
      allowEmpty: true,
    })
      .then((res) => {
        if (cancelled) return
        setSearchResults(res.success && res.data ? res.data : [])
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedManualSearch, effectiveManualMode, searchOtherClusters])

  // When nearby recommendations are empty, default cluster search on once per empty-primary entry.
  useEffect(() => {
    if (!effectiveManualMode) {
      autoEnabledClusterSearchRef.current = false
      return
    }
    if (
      effectiveManualMode === 'primary' &&
      !loading &&
      recommendations.length === 0 &&
      !autoEnabledClusterSearchRef.current
    ) {
      autoEnabledClusterSearchRef.current = true
      setSearchOtherClusters(true)
    }
  }, [effectiveManualMode, loading, recommendations.length])

  useEffect(() => {
    if (effectiveManualMode && effectiveManualMode !== prevManualModeRef.current) {
      onManualModeOpen?.()
    }
    prevManualModeRef.current = effectiveManualMode
  }, [effectiveManualMode, onManualModeOpen])

  const manualListRecommendations = useMemo(() => {
    const rankedIds = new Set(
      recommendations.map((rec) => rec.user_id).filter((id): id is number => id != null),
    )
    const merged = [...recommendations]
    for (const rec of searchResults) {
      if (rec.user_id != null && !rankedIds.has(rec.user_id)) {
        merged.push(rec)
      }
    }
    const q = manualSearchQuery.trim().toLowerCase()
    if (!q) return merged
    return merged.filter((rec) => {
      const haystack = [rec.full_name, rec.cluster, rec.launch_point]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [recommendations, searchResults, manualSearchQuery])

  const visibleManualListRecommendations = useMemo(
    () => manualListRecommendations.slice(0, manualListVisibleCount),
    [manualListRecommendations, manualListVisibleCount],
  )
  const hasMoreManualListItems = manualListRecommendations.length > manualListVisibleCount

  useEffect(() => {
    setManualListVisibleCount(MANUAL_LIST_PAGE_SIZE)
  }, [effectiveManualMode, manualSearchQuery, debouncedManualSearch])

  const wrapColor = useMemo(() => {
    if (panelState === 'editing') return { border: 'orange.300', headerBg: 'orange.50', headerBorder: 'orange.200', accent: 'orange.600' }
    if (panelState === 'conflict') return { border: 'red.300', headerBg: 'red.50', headerBorder: 'red.200', accent: 'red.600' }
    if (panelState === 'resolved-swap' || panelState === 'resolved-new' || panelState === 'resolved-keep')
      return { border: 'green.300', headerBg: 'green.50', headerBorder: 'green.200', accent: 'green.700' }
    return { border: 'blue.300', headerBg: 'blue.50', headerBorder: 'blue.200', accent: 'blue.700' }
  }, [panelState])

  const headerTitle = useMemo(() => {
    if (panelState === 'conflict') return 'Field engineer dispatch — Conflict'
    if (panelState === 'resolved-swap' || panelState === 'resolved-new') return 'Field engineer dispatch — Resolved'
    if (panelState === 'change-primary') return 'Select primary below'
    if (panelState === 'change-additional') return 'Select additional FE below'
    return 'Field engineer dispatch'
  }, [panelState])

  const primaryConflict =
    panelState === 'conflict' && conflicts && !conflicts.primary.available

  const handleManualSelect = (rec: FieldEngineerRecommendation) => {
    const uid = rec.user_id ?? null
    if (uid == null) return

    if (effectiveManualMode === 'primary') {
      const conflict = getEngineerAssignmentConflict('primary', uid, null, additionalIds)
      if (conflict) {
        showError(conflict)
        return
      }
      suppressAutoAssignRef.current = true
      autoAssignedRef.current = true
      onSelectPrimary(rec)
      if (wantsAdditional && additionalSlots.length === 0) {
        autoAdditionalRef.current = false
        panelStateRef.current = 'change-primary'
        setPanelState('change-primary')
      } else {
        panelStateRef.current = 'change-primary'
        setPanelState('change-primary')
      }
    } else if (effectiveManualMode === 'additional') {
      const slot = editingAdditionalIndex ?? additionalSlots.length
      const conflict = getEngineerAssignmentConflict(
        'additional',
        uid,
        primaryEngineerId,
        additionalIds.filter((_, index) => index !== slot),
      )
      if (conflict) {
        showError(conflict)
        return
      }
      autoAdditionalRef.current = true
      onSelectAdditional(rec, slot)
      setEditingAdditionalIndex(null)
      panelStateRef.current = 'with-additional'
      setPanelState('with-additional')
    }
  }

  const handleActivityFieldChange = (fromLocal: string, toLocal: string) => {
    onActivityScheduleChange?.(fromLocal, toLocal)
  }
  const [manualEndOverride, setManualEndOverride] = useState(initialManualEndOverride)
  const [manualEndOverrideReason, setManualEndOverrideReason] = useState(initialManualEndOverrideReason)

  useEffect(() => {
    setManualEndOverride(initialManualEndOverride)
    setManualEndOverrideReason(initialManualEndOverrideReason)
  }, [initialManualEndOverride, initialManualEndOverrideReason])

  const supportDurationMinutes = useMemo(() => {
    if (!activityScheduleFrom || !activityScheduleTo) return 240
    const difference = new Date(activityScheduleTo).getTime() - new Date(activityScheduleFrom).getTime()
    return difference > 0 ? Math.round(difference / 60000) : 240
  }, [activityScheduleFrom, activityScheduleTo])

  const handleSupportDurationChange = (minutes: number) => {
    if (!activityScheduleFrom || !Number.isFinite(minutes) || minutes <= 0) return
    const start = new Date(activityScheduleFrom)
    const end = new Date(start.getTime() + minutes * 60000)
    const pad = (value: number) => String(value).padStart(2, '0')
    const endLocal = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`
    handleActivityFieldChange(activityScheduleFrom, endLocal)
  }

  const handlePromoteAdditional = () => {
    skipAutoCheckRef.current = false
    onPromoteAdditional()
    setPanelState('resolved-swap')
    setConflicts(null)
  }

  const handleFindNewPrimary = () => {
    skipAutoCheckRef.current = false
    onFindNewPrimary()
    setPanelState('change-primary')
    setConflicts(null)
  }

  const handleKeepAnyway = () => {
    skipAutoCheckRef.current = true
    setPanelState('resolved-keep')
    setConflicts(null)
    onKeepAnyway?.()
  }

  const handleClearAdditionalSlot = async (slotIndex: number) => {
    if (clearingAssignment) return
    setClearingAssignment(true)
    try {
      const result = await onClearAdditional(slotIndex)
      if (result === false) return
      autoAdditionalRef.current = true
      if (additionalSlots.length <= 1) {
        if (!primaryEngineerId) {
          suppressAutoAssignRef.current = true
          setWantsAdditional(false)
          setEditingAdditionalIndex(null)
          panelStateRef.current = 'change-primary'
          setPanelState('change-primary')
        } else {
          // Keep primary; open backup pick. Suppress primary auto-assign/toast if recommend refreshes.
          suppressAutoAssignRef.current = true
          setEditingAdditionalIndex(0)
          panelStateRef.current = 'change-additional'
          setPanelState('change-additional')
        }
      } else {
        setEditingAdditionalIndex(null)
        setPanelState('with-additional')
        // Manual list is not opened — refresh ranked list once here.
        onRefresh?.()
      }
    } finally {
      setClearingAssignment(false)
    }
  }

  const handleClearPrimaryClick = async () => {
    if (clearingAssignment) return
    suppressAutoAssignRef.current = true
    autoAssignedRef.current = false
    setClearingAssignment(true)
    try {
      const result = await onClearPrimary()
      if (result === false) return
      panelStateRef.current = 'change-primary'
      setPanelState('change-primary')
    } finally {
      setClearingAssignment(false)
    }
  }

  const handleAddAdditional = () => {
    autoAdditionalRef.current = true
    onAddAdditionalSlot?.()
    const next = findNextEligible()
    if (next) {
      onSelectAdditional(next, additionalSlots.length)
    } else {
      setEditingAdditionalIndex(additionalSlots.length)
      panelStateRef.current = 'change-additional'
      setPanelState('change-additional')
    }
  }

  const handleAddAdditionalManual = () => {
    autoAdditionalRef.current = true
    onAddAdditionalSlot?.()
    setEditingAdditionalIndex(additionalSlots.length)
    panelStateRef.current = 'change-additional'
    setPanelState('change-additional')
  }

  const showActivitySchedule =
    onActivityScheduleChange &&
    (activityScheduleFrom || activityScheduleTo || preferredScheduleAt)

  const activityRangeLabel = formatActivityLabel(activityScheduleFrom, activityScheduleTo)

  const allAdditionalSlotsFilled = additionalSlots.length > 0 && additionalSlots.every((slot) => slot.id != null)

  const renderAdditionalPickPrompt = (slotIndex: number) => (
    <Box
      key={`additional-pick-${slotIndex}`}
      border="1.5px dashed"
      borderColor="green.300"
      borderRadius="md"
      p={2.5}
      mt={1.5}
      bg="transparent"
    >
      <Text fontSize="10px" fontWeight="500" color="green.700">
        {additionalFeOrdinalLabel(slotIndex)}
      </Text>
      <Text fontSize="11px" color={colors.textMuted} mt="3px">
        {findNextEligible() ? (
          <>
            Tap{' '}
            <Box as="span" bg="green.600" color="white" borderRadius="full" px="5px" fontSize="9px">
              B
            </Box>{' '}
            on a card below
          </>
        ) : (
          'No additional engineers available for this FE slot.'
        )}
      </Text>
    </Box>
  )

  const renderAdditionalCard = (slot: AdditionalSlot, slotIndex: number) => {
    if (assigningAdditionalSlotIndex === slotIndex) {
      return (
        <SlotPreloader
          key={`additional-loading-${slotIndex}`}
          label={additionalFeOrdinalLabel(slotIndex)}
          message="Selecting additional FE…"
          accent="green"
        />
      )
    }

    if (panelState === 'change-additional' && editingAdditionalIndex === slotIndex) {
      return renderAdditionalPickPrompt(slotIndex)
    }

    const rec = findRecByUserId(recommendations, slot.id)
    const name = rec?.full_name ?? slot.name

    if (slot.id != null && name) {
      return (
        <Box
          key={`additional-${slotIndex}-${slot.id}`}
          border="0.5px solid"
          borderColor="green.300"
          borderRadius="md"
          p={2.5}
          mt={1.5}
          bg="green.50"
        >
          <HStack spacing={1} mb="2px">
            <CheckCircle size={10} color="green.700" />
            <Text fontSize="10px" fontWeight="500" color="green.700">
              {additionalFeOrdinalLabel(slotIndex)}
            </Text>
          </HStack>
          <HStack justify="space-between" align="flex-start">
            <Box>
              <Text fontSize="13px" fontWeight="500">
                {name}
              </Text>
              {rec ? (
                <Text fontSize="10px" color={colors.textSecondary} mt="1px">
                  {rec.cluster ?? rec.launch_point ?? '—'} · {formatDistance(rec.distance_km, rec.distance_source)} ·{' '}
                  {formatTravelMinutes(rec.apt_travel_minutes)}
                </Text>
              ) : null}
              <HStack mt={1} flexWrap="wrap" gap={1}>
                <Tag variant="green">{`Auto-assigned · #${slotIndex + 2}`}</Tag>
                {rec ? (
                  <Tag variant="neutral">
                    APT {formatAptMinutes(rec.apt_travel_minutes, rec.apt_repair_minutes)}
                  </Tag>
                ) : null}
              </HStack>
                  {!isReadOnly ? (
                <HStack mt={1} spacing={2}>
                  <Button
                    size="xs"
                    variant="link"
                    color="red.500"
                    isDisabled={clearingAssignment}
                    onClick={() => {
                      void handleClearAdditionalSlot(slotIndex)
                    }}
                  >
                    Clear
                  </Button>
                </HStack>
              ) : null}
            </Box>
            <Box
              w="32px"
              h="32px"
              borderRadius="full"
              bg="green.100"
              color="green.700"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="12px"
              fontWeight="500"
              flexShrink={0}
            >
              {getInitials(name)}
            </Box>
          </HStack>
        </Box>
      )
    }

    return renderAdditionalPickPrompt(slotIndex)
  }

  return (
    <VStack align="stretch" spacing={2} mt={3}>
      {/* Context strip */}
      {(displayReferenceName || displayReferenceCode || areaType || hardwareType) && (
        <HStack
          flexWrap="wrap"
          gap={1.5}
          px={3}
          py={1.5}
          border="0.5px solid"
          borderColor={colors.borderLight}
          borderRadius="md"
          bg={colors.cardBg}
          fontSize="11px"
          color={colors.textSecondary}
        >
          <Info size={13} color={colors.brandBlue} style={{ flexShrink: 0 }} />
          <Text as="span">For:</Text>
          {displayReferenceCode || displayReferenceName ? (
            <Text as="strong" color={colors.text} fontWeight="600">
              {displayReferenceCode ? `${displayReferencePrefix} ${displayReferenceCode}` : displayReferencePrefix}
              {displayReferenceName ? `${displayReferenceCode ? ' — ' : ': '}${displayReferenceName}` : ''}
            </Text>
          ) : null}
          {areaType ? (
            <>
              <Box w="3px" h="3px" borderRadius="full" bg="gray.400" flexShrink={0} />
              <Text as="strong" color={colors.text} fontWeight="600">
                {areaType}
              </Text>
            </>
          ) : null}
          {hardwareType ? (
            <>
              <Box w="3px" h="3px" borderRadius="full" bg="gray.400" flexShrink={0} />
              <Text as="strong" color={colors.text} fontWeight="600">
                {hardwareType}
              </Text>
            </>
          ) : null}
          {!isReadOnly && onEditStore ? (
            <Button
              ml="auto"
              size="xs"
              variant="link"
              color={colors.brandBlue}
              fontSize="10px"
              onClick={onEditStore}
            >
              Edit ↗
            </Button>
          ) : null}
        </HStack>
      )}
      {storeSearchSlot ? (
        <Box position="relative" zIndex={2}>
          {storeSearchSlot}
        </Box>
      ) : null}

      {/* Main panel */}
      <Box
        border="0.5px solid"
        borderColor={wrapColor.border}
        borderRadius="12px"
        overflow="hidden"
        position="relative"
        zIndex={1}
      >
        <HStack
          justify="space-between"
          px={3.5}
          py={2}
          borderBottom="0.5px solid"
          borderColor={wrapColor.headerBorder}
          bg={wrapColor.headerBg}
        >
          <HStack spacing={1.5}>
            <Truck size={15} color={wrapColor.accent} />
            <Text fontSize="12px" fontWeight="500" color={wrapColor.accent}>
              {headerTitle}
            </Text>
          </HStack>
          <Tag variant="green">
            <Zap size={10} />
            Auto-enabled
          </Tag>
        </HStack>

        <Box px={3.5} py={3} bg={colors.sectionBg}>
          {/* Activity schedule */}
          {showActivitySchedule ? (
            <Box mb={2.5}>
              {panelState === 'conflict' ? (
                <HStack
                  p={2.5}
                  borderRadius="md"
                  border="0.5px solid"
                  borderColor="red.200"
                  bg="red.50"
                  align="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <AlertTriangle size={14} color="#c53030" style={{ flexShrink: 0 }} />
                  <Box flex={1}>
                    <Text fontSize="11px" fontWeight="500" color="red.700">
                      Schedule conflict — {activityRangeLabel}
                    </Text>
                    <Text fontSize="10px" color="red.600" mt="1px">
                      Primary engineer unavailable. Resolve below.
                    </Text>
                  </Box>
                </HStack>
              ) : scheduleBannerState === 'checking' ? (
                <HStack p={2.5} spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="11px" color={colors.textSecondary}>
                    Checking engineer availability…
                  </Text>
                </HStack>
              ) : (
                <Box
                  p={2.5}
                  borderRadius="md"
                  border="0.5px solid"
                  borderColor="blue.200"
                  bg="blue.50"
                >
                  <HStack mb={2} spacing={1.5}>
                    <CalendarCheck size={14} color={colors.brandBlue} />
                    <Text fontSize="11px" fontWeight="500" color="blue.800">
                      Activity schedule
                    </Text>
                  </HStack>
                  <Text fontSize="10px" color={colors.textSecondary} mb={2}>
                    Select the start and support duration. The end date and time are calculated automatically.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} alignItems="start">
                    <Box minW={0}>
                      <Text fontSize="10px" fontWeight="500" mb={1}>
                        Start date &amp; time
                      </Text>
                      <Input
                        type="datetime-local"
                        size="sm"
                        fontSize="12px"
                        value={activityScheduleFrom ?? ''}
                        isReadOnly={isReadOnly}
                        onChange={(e) => {
                          const nextStart = e.target.value
                          const start = new Date(nextStart)
                          const end = new Date(start.getTime() + supportDurationMinutes * 60000)
                          const pad = (value: number) => String(value).padStart(2, '0')
                          const nextEnd = Number.isNaN(end.getTime()) ? '' : `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`
                          handleActivityFieldChange(nextStart, nextEnd)
                        }}
                        bg={colors.cardBg}
                      />
                    </Box>
                    <Box minW={0}>
                      <Text fontSize="10px" fontWeight="500" mb={1}>
                        Support duration
                      </Text>
                      <Select
                        size="sm"
                        fontSize="12px"
                        value={supportDurationMinutes}
                        isReadOnly={isReadOnly}
                        onChange={(e) => handleSupportDurationChange(Number(e.target.value))}
                        bg={colors.cardBg}
                      >
                        {(supportDurationOptions?.length ? supportDurationOptions : [
                          { code: '60', label: '1 hour' },
                          { code: '120', label: '2 hours' },
                          { code: '180', label: '3 hours' },
                          { code: '240', label: '4 hours' },
                          { code: '300', label: '5 hours' },
                          { code: '360', label: '6 hours' },
                          { code: '420', label: '7 hours' },
                          { code: '480', label: '8 hours' },
                          { code: '540', label: '9 hours' },
                          { code: '600', label: '10 hours' },
                        ]).map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
                      </Select>
                    </Box>
                    <Box minW={0}>
                      <HStack justify="space-between" minH="15px" mb={1} spacing={2}>
                        <Text fontSize="10px" fontWeight="500">End date &amp; time</Text>
                        {!manualEndOverride ? (
                          <HStack spacing={1} color={colors.textMuted}>
                            <Lock size={10} />
                            <Text fontSize="9px" fontWeight="600">Read only</Text>
                          </HStack>
                        ) : null}
                      </HStack>
                      <Input
                        type="datetime-local"
                        size="sm"
                        fontSize="12px"
                        value={activityScheduleTo ?? ''}
                        isReadOnly={!manualEndOverride || isReadOnly}
                        onChange={(e) => onActivityScheduleChange?.(activityScheduleFrom ?? '', e.target.value, manualEndOverrideReason)}
                        bg={manualEndOverride && !isReadOnly ? colors.cardBg : 'gray.100'}
                        color={manualEndOverride && !isReadOnly ? colors.text : 'gray.600'}
                        borderColor={manualEndOverride && !isReadOnly ? colors.border : 'gray.300'}
                        cursor={manualEndOverride && !isReadOnly ? 'text' : 'not-allowed'}
                        _readOnly={{ bg: 'gray.100', color: 'gray.600', borderColor: 'gray.300' }}
                      />
                      <HStack justify="space-between" mt={1} minH="20px" align="center">
                        <Text fontSize="9px" color={manualEndOverride ? 'orange.600' : colors.textMuted}>
                          {manualEndOverride ? 'Manually overridden' : 'Calculated automatically from start and duration'}
                        </Text>
                        {!isReadOnly ? (
                          <Button
                            h="20px"
                            minW="auto"
                            px={1.5}
                            size="xs"
                            variant="link"
                            colorScheme="blue"
                            onClick={() => {
                              if (manualEndOverride) {
                                const configuredDurations = (supportDurationOptions ?? []).map((option) => Number(option.code))
                                const calculatedDuration = configuredDurations.includes(supportDurationMinutes) ? supportDurationMinutes : 240
                                handleSupportDurationChange(calculatedDuration)
                              }
                              setManualEndOverride((value) => !value)
                              setManualEndOverrideReason('')
                            }}
                          >
                            {manualEndOverride ? 'Use calculated end' : 'Override'}
                          </Button>
                        ) : null}
                      </HStack>
                    </Box>
                  </SimpleGrid>
                  {manualEndOverride ? (
                    <Box mt={2}>
                      <Text fontSize="10px" fontWeight="500" mb={1}>Override reason *</Text>
                      <Input
                        size="sm"
                        value={manualEndOverrideReason}
                        placeholder="Explain why the calculated end time is being changed"
                        onChange={(e) => {
                          const reason = e.target.value
                          setManualEndOverrideReason(reason)
                          onActivityScheduleChange?.(activityScheduleFrom ?? '', activityScheduleTo ?? '', reason)
                        }}
                      />
                    </Box>
                  ) : null}
                  {!isReadOnly && onActivityScheduleApply ? (
                    <HStack justify="flex-end" mt={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        isDisabled={
                          !activityScheduleFrom ||
                          !activityScheduleTo ||
                          !isActivityWindowValid(activityScheduleFrom, activityScheduleTo) ||
                          (manualEndOverride && !manualEndOverrideReason.trim())
                        }
                        onClick={() => onActivityScheduleApply(activityScheduleFrom ?? '', activityScheduleTo ?? '')}
                      >
                        Apply schedule
                      </Button>
                    </HStack>
                  ) : null}
                  {activityScheduleFrom &&
                  activityScheduleTo &&
                  !isActivityWindowValid(activityScheduleFrom, activityScheduleTo) ? (
                    <Text fontSize="10px" color="red.600" mt={2}>
                      End time must be after start time.
                    </Text>
                  ) : null}
                </Box>
              )}
            </Box>
          ) : null}

          {/* Cert row */}
          {hasCertRequirement ? (
          <HStack justify="space-between" mb={2.5} flexWrap="wrap" gap={2}>
            <HStack spacing={1.5} fontSize="11px">
              <Box w="28px" h="16px" borderRadius="8px" bg={colors.brandBlue} position="relative" flexShrink={0}>
                <Box
                  position="absolute"
                  w="10px"
                  h="10px"
                  bg="white"
                  borderRadius="full"
                  top="2px"
                  left="15px"
                />
              </Box>
              <Text color={colors.brandBlue}>
                Certification required:{' '}
                <Text as="strong">{certLabel ?? '—'}</Text>
              </Text>
            </HStack>
            <HStack fontSize="10px" color={colors.textMuted} spacing={2}>
              <HStack spacing={1}>
                <Box as="span" bg={colors.brandBlue} color="white" borderRadius="full" px="5px" fontSize="9px">
                  P
                </Box>
                <Text>Primary</Text>
              </HStack>
              <HStack spacing={1}>
                <Box as="span" bg="green.600" color="white" borderRadius="full" px="5px" fontSize="9px">
                  B
                </Box>
                <Text>Additional FE</Text>
              </HStack>
            </HStack>
          </HStack>
          ) : null}

          {/* Map — only after site pin is confirmed (avoids a second map while placing the pin) */}
          {showOverviewMap ? (
          <Box position="relative">
            <FEDispatchMap
              recommendations={recommendations}
              primaryUserId={primaryEngineerId}
              additionalUserId={firstAdditionalId}
              referenceType={referenceType}
              referenceLabel={displayReferenceName}
              storeCode={displayReferenceCode}
              storeLatitude={storeLatitude}
              storeLongitude={storeLongitude}
              conflictUserId={primaryConflict ? primaryEngineerId : null}
              onPinClick={
                effectiveManualMode && !isReadOnly
                  ? (rec) => handleManualSelect(rec)
                  : undefined
              }
            />
            {loading ? (
              <Box
                position="absolute"
                inset={0}
                zIndex={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
                bg="blackAlpha.200"
                borderRadius="md"
              >
                <Spinner size="sm" color={colors.brandBlue} />
                <Text fontSize="11px" color={colors.textSecondary} bg={colors.cardBg} px={2} py={1} borderRadius="md">
                  Finding field engineers...
                </Text>
              </Box>
            ) : null}
          </Box>
          ) : null}

          {/* Primary slot */}
          {assigningPrimary || (Boolean(loading) && !primaryEngineerId) ? (
            <SlotPreloader
              label="Primary engineer"
              message={loading && !assigningPrimary ? 'Finding field engineers…' : 'Selecting primary engineer…'}
              accent="blue"
            />
          ) : !primaryEngineerId &&
            recommendations.some((r) => !r.is_locked && r.user_id != null && Number(r.user_id) > 0) &&
            !isReadOnly &&
            !disableAutoAssignWhenAssigned &&
            panelState === 'auto' &&
            (!autoAssignedRef.current || assigningPrimary) ? (
            <SlotPreloader
              label="Primary engineer"
              message="Selecting primary engineer…"
              accent="blue"
            />
          ) : panelState === 'change-primary' ||
            (effectiveManualMode === 'primary' && !primaryEngineerId) ? (
            <Box
              border="0.5px dashed"
              borderColor="blue.300"
              borderRadius="md"
              p={2.5}
              mb={2}
              bg="transparent"
            >
              <Text fontSize="10px" fontWeight="500" color={colors.brandBlue}>
                Primary engineer
              </Text>
              {!loading && recommendations.length === 0 ? (
                <Text fontSize="11px" color={colors.textMuted} mt="3px">
                  No nearby engineers — search other clusters or pick manually.
                </Text>
              ) : (
                <Text fontSize="11px" color={colors.textMuted} mt="3px">
                  Tap{' '}
                  <Box as="span" bg={colors.brandBlue} color="white" borderRadius="full" px="5px" fontSize="9px">
                    P
                  </Box>{' '}
                  on a card below
                </Text>
              )}
            </Box>
          ) : primaryConflict && primaryRec ? (
            <Box border="0.5px solid" borderColor="red.300" borderRadius="md" p={2.5} mb={2} bg="red.50">
              <HStack spacing={1} mb={1}>
                <AlertCircle size={10} color="#c53030" />
                <Text fontSize="10px" fontWeight="500" color="red.600">
                  Primary — conflict
                </Text>
              </HStack>
              <HStack justify="space-between" align="flex-start">
                <Box>
                  <Text fontSize="13px" fontWeight="500">
                    {primaryRec.full_name}
                  </Text>
                  <HStack fontSize="10px" color="red.600" mt="3px" spacing={1}>
                    <XCircle size={10} />
                    <Text>{conflicts?.primary.conflict_reason ?? 'Unavailable'}</Text>
                  </HStack>
                </Box>
                <Box
                  w="30px"
                  h="30px"
                  borderRadius="full"
                  bg="blue.50"
                  color="blue.700"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="11px"
                  fontWeight="500"
                  flexShrink={0}
                >
                  {getInitials(primaryRec.full_name)}
                </Box>
              </HStack>
              {!isReadOnly ? (
                <HStack mt={2} flexWrap="wrap" gap={1}>
                  {additionalIds.length > 0 ? (
                    <Button size="xs" colorScheme="blue" onClick={handlePromoteAdditional}>
                      Promote to Primary
                    </Button>
                  ) : null}
                  <Button size="xs" variant="outline" colorScheme="red" onClick={handleFindNewPrimary}>
                    Find new Primary
                  </Button>
                  <Button size="xs" variant="ghost" onClick={handleKeepAnyway}>
                    Keep anyway
                  </Button>
                </HStack>
              ) : null}
            </Box>
          ) : primaryEngineerId && (primaryRec || primaryEngineerName) ? (
            <Box border="0.5px solid" borderColor="blue.300" borderRadius="md" p={2.5} mb={2} bg="blue.50">
              <Text fontSize="10px" fontWeight="500" color={colors.brandBlue} mb="2px">
                Primary engineer
              </Text>
              <HStack justify="space-between" align="flex-start">
                <Box>
                  <Text fontSize="13px" fontWeight="500">
                    {primaryRec?.full_name ?? primaryEngineerName}
                  </Text>
                  {primaryRec ? (
                    <Text fontSize="10px" color={colors.textSecondary} mt="1px">
                      {formatPrimaryMetaLine(primaryRec)}
                      {primaryRec.workload != null || primaryRec.daily_capacity != null
                        ? ` · ${primaryRec.workload ?? '—'}/${primaryRec.daily_capacity ?? '—'} tickets`
                        : ''}
                    </Text>
                  ) : null}
                  <HStack mt={1} flexWrap="wrap" gap={1}>
                    <Tag variant="green">
                      {panelState === 'resolved-swap' ? 'Promoted' : 'Auto-assigned · #1'}
                    </Tag>
                    {primaryRec && hasCertRequirement ? (
                      primaryRec.has_required_certifications !== false ? (
                        <Tag variant="blue">Cert ✓</Tag>
                      ) : (
                        <Tag variant="red">No cert</Tag>
                      )
                    ) : null}
                    {primaryRec ? (
                      <>
                        <Tag variant="neutral">
                          APT {formatAptMinutes(primaryRec.apt_travel_minutes, primaryRec.apt_repair_minutes)}
                        </Tag>
                        <Tag variant="neutral">Score {Math.round(primaryRec.score)}</Tag>
                      </>
                    ) : null}
                  </HStack>
                  {!isReadOnly ? (
                    <HStack mt={1} spacing={2}>
                      <Button
                        size="xs"
                        variant="link"
                        color="red.500"
                        isDisabled={clearingAssignment}
                        onClick={() => {
                          void handleClearPrimaryClick()
                        }}
                      >
                        Clear
                      </Button>
                    </HStack>
                  ) : null}
                </Box>
                <Box
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bg="blue.100"
                  color="blue.700"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="12px"
                  fontWeight="500"
                  flexShrink={0}
                >
                  {getInitials(primaryRec?.full_name ?? primaryEngineerName ?? '?')}
                </Box>
              </HStack>
            </Box>
          ) : null}

          {/* Additional FE checkbox row */}
          <HStack
            border="0.5px solid"
            borderColor={wantsAdditional ? 'green.300' : colors.borderLight}
            borderRadius="md"
            p={2}
            bg={wantsAdditional ? 'green.50' : colors.cardBg}
            mb={wantsAdditional ? 1.5 : 0}
          >
            <Checkbox
              isChecked={wantsAdditional}
              isDisabled={isReadOnly}
              colorScheme="green"
              onChange={(e) => {
                if (e.target.checked) {
                  setWantsAdditional(true)
                  autoAdditionalRef.current = false
                  if (!primaryEngineerId) {
                    panelStateRef.current = 'change-primary'
                    setPanelState('change-primary')
                  } else {
                    panelStateRef.current = 'with-additional'
                    setPanelState('with-additional')
                  }
                } else {
                  setWantsAdditional(false)
                  autoAdditionalRef.current = false
                  setEditingAdditionalIndex(null)
                  const nextState = primaryEngineerId ? 'auto' : 'change-primary'
                  panelStateRef.current = nextState
                  setPanelState(nextState)
                }
              }}
            >
              <Text fontSize="12px" fontWeight="500">
                Assign additional FE?
              </Text>
            </Checkbox>
            <Text
              ml="auto"
              fontSize="10px"
              color={additionalIds.length > 0 ? 'green.600' : wantsAdditional ? colors.brandBlue : colors.textMuted}
            >
              {additionalIds.length > 0
                ? `${additionalIds.length} assigned`
                : wantsAdditional
                  ? 'Select below ↓'
                  : 'Optional'}
            </Text>
          </HStack>

          {/* Additional FE slots */}
          {wantsAdditional ? (
            <>
              {additionalSlots.map((slot, index) => renderAdditionalCard(slot, index))}
              {additionalSlots.length === 0 && panelState === 'change-additional'
                ? renderAdditionalPickPrompt(0)
                : null}
              {additionalSlots.length === 0 && panelState !== 'change-additional'
                ? Boolean(loading) ||
                  assigningAdditionalSlotIndex === 0 ||
                  (Boolean(primaryEngineerId) &&
                    recommendations.length > 0 &&
                    !isReadOnly &&
                    Boolean(findNextEligible()) &&
                    (!autoAdditionalRef.current || assigningAdditionalSlotIndex === 0))
                  ? (
                      <SlotPreloader
                        label="2nd FE"
                        message={
                          loading && assigningAdditionalSlotIndex !== 0
                            ? 'Finding field engineers…'
                            : 'Selecting additional FE…'
                        }
                        accent="green"
                      />
                    )
                  : renderAdditionalPickPrompt(0)
                : null}
              {panelState === 'change-additional' && editingAdditionalIndex === additionalSlots.length && additionalSlots.length > 0
                ? renderAdditionalPickPrompt(additionalSlots.length)
                : null}
              {!isReadOnly && allAdditionalSlotsFilled && panelState !== 'change-additional' ? (
                <HStack mt={1.5} spacing={2} flexWrap="wrap">
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="green"
                    leftIcon={<Zap size={12} />}
                    onClick={handleAddAdditional}
                  >
                    Auto-assign another
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="green"
                    leftIcon={<Search size={12} />}
                    onClick={handleAddAdditionalManual}
                  >
                    Select manually
                  </Button>
                </HStack>
              ) : null}
            </>
          ) : null}

          {/* Manual panel */}
          {effectiveManualMode && !isReadOnly ? (
            <Box mt={2.5} border="0.5px solid" borderColor={colors.borderLight} borderRadius="md" overflow="hidden">
              <HStack
                justify="space-between"
                px={3}
                py={1.5}
                bg={effectiveManualMode === 'primary' ? 'blue.50' : 'green.50'}
                borderBottom="0.5px solid"
                borderColor={effectiveManualMode === 'primary' ? 'blue.200' : 'green.200'}
              >
                <HStack spacing={1}>
                  <Search size={13} color={effectiveManualMode === 'primary' ? colors.brandBlue : 'green.700'} />
                  <Text
                    fontSize="12px"
                    fontWeight="500"
                    color={effectiveManualMode === 'primary' ? colors.brandBlue : 'green.700'}
                  >
                    {effectiveManualMode === 'primary' ? 'Select a Primary engineer' : 'Select an Additional FE'}
                  </Text>
                </HStack>
                <Button
                  size="xs"
                  variant="outline"
                  fontSize="10px"
                  leftIcon={<X size={10} />}
                  onClick={() => {
                    setEditingAdditionalIndex(null)
                    if (effectiveManualMode === 'primary') {
                      setPanelState(wantsAdditional || additionalIds.length > 0 ? 'with-additional' : 'auto')
                    } else {
                      setPanelState('with-additional')
                    }
                  }}
                >
                  Close
                </Button>
              </HStack>

              <Box px={3} py={2.5} bg={colors.sectionBg} position="relative" minH={isManualListBusy ? '180px' : undefined}>
                <Box
                  opacity={isManualListBusy ? 0.4 : 1}
                  pointerEvents={isManualListBusy ? 'none' : 'auto'}
                  transition="opacity 0.15s ease"
                  aria-busy={isManualListBusy}
                >
                <HStack mb={2} fontSize="10px" color={colors.textMuted} flexWrap="wrap" gap={2}>
                  <HStack spacing={1}>
                    <Box w="10px" h="4px" borderRadius="2px" bg={colors.brandBlue} />
                    <Text>Requested</Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Box w="10px" h="4px" borderRadius="2px" bg="green.500" />
                    <Text>Free</Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Box w="10px" h="4px" borderRadius="2px" bg="gray.300" />
                    <Text>Busy</Text>
                  </HStack>
                  <Text ml="auto" fontSize="10px">
                    8AM ——————————— 6PM ·{' '}
                    <Text as="strong">{activityRangeLabel}</Text>
                  </Text>
                </HStack>

                <HStack mb={2} spacing={2}>
                  <Box position="relative" flex={1}>
                    <Input
                      size="sm"
                      placeholder="Search all field engineers…"
                      value={manualSearchQuery}
                      onChange={(e) => setManualSearchQuery(e.target.value)}
                      pl={8}
                      fontSize="12px"
                      bg={colors.cardBg}
                      isDisabled={isManualListBusy}
                    />
                    <Box position="absolute" left={2.5} top="50%" transform="translateY(-50%)" pointerEvents="none">
                      <Search size={14} color={colors.textMuted} />
                    </Box>
                  </Box>
                </HStack>
                <Checkbox
                  size="sm"
                  mb={2}
                  isChecked={searchOtherClusters}
                  onChange={(event) => setSearchOtherClusters(event.target.checked)}
                  isDisabled={isManualListBusy}
                >
                  <Text fontSize="11px">
                    Search other area clusters (manual override)
                  </Text>
                </Checkbox>

                <VStack align="stretch" spacing={1.5}>
                  {manualListRecommendations.length === 0 && !isManualListBusy ? (
                    <Text fontSize="11px" color={colors.textMuted} py={2} textAlign="center">
                      {manualSearchQuery.trim()
                        ? `No engineers match "${manualSearchQuery.trim()}"`
                        : 'No field engineers available'}
                    </Text>
                  ) : null}
                  {visibleManualListRecommendations.map((fe, idx) => {
                    const uid = fe.user_id ?? null
                    const isP = uid != null && uid === primaryEngineerId
                    const additionalPos = uid != null ? additionalIds.indexOf(uid) : -1
                    const isAdditional = additionalPos >= 0
                    const isConflictFe = primaryConflict && uid === primaryEngineerId
                    const isLocked = Boolean(fe.is_locked) && !isP && !isAdditional
                    const dimmed =
                      !isManualSelecting &&
                      ((!fe.available_at_schedule && !isP && !isAdditional && !isLocked) ||
                        (hasCertRequirement && fe.has_required_certifications === false && !isP && !isAdditional))
                    const isPrimaryPickBlocked =
                      effectiveManualMode === 'additional' && uid != null && uid === primaryEngineerId
                    const isAdditionalPickBlocked =
                      effectiveManualMode === 'primary' && uid != null && additionalIds.includes(uid)
                    const isAlreadyAdditionalBlocked = effectiveManualMode === 'additional' && isAdditional
                    const isPickBlocked =
                      isPrimaryPickBlocked || isAdditionalPickBlocked || isAlreadyAdditionalBlocked

                    const aptTotal = (fe.apt_travel_minutes ?? 0) + (fe.apt_repair_minutes ?? 0)
                    const tl = buildTimelineSegments(
                      uid != null ? feAvailabilityByUser?.[uid] : undefined,
                      scheduleDateStr,
                      preferredScheduleAt,
                      aptTotal || 90,
                    )

                    const cardBorder = isP
                      ? 'blue.300'
                      : isAdditional
                        ? 'green.300'
                        : isConflictFe
                          ? 'red.300'
                          : isLocked
                            ? 'orange.300'
                            : colors.borderLight
                    const cardBg = isP
                      ? 'blue.50'
                      : isAdditional
                        ? 'green.50'
                        : isConflictFe
                          ? 'red.50'
                          : isLocked
                            ? 'orange.50'
                            : colors.cardBg
                    const hoverBg = isP
                      ? 'blue.100'
                      : isAdditional
                        ? 'green.100'
                        : isConflictFe
                          ? 'red.100'
                          : isLocked
                            ? 'orange.100'
                            : 'gray.50'
                    const hoverBorder = isP
                      ? 'blue.400'
                      : isAdditional
                        ? 'green.400'
                        : isConflictFe
                          ? 'red.400'
                          : isLocked
                            ? 'orange.400'
                            : colors.brandBlue

                    const cbLabel = isP
                      ? 'P'
                      : isAdditional
                        ? `B${additionalPos + 1}`
                        : isLocked
                          ? '🔒'
                          : effectiveManualMode === 'additional'
                            ? 'B'
                            : '+'
                    const cbBg = isP
                      ? colors.brandBlue
                      : isAdditional
                        ? 'green.600'
                        : isConflictFe
                          ? 'red.100'
                          : isLocked
                            ? 'orange.100'
                            : colors.cardBg
                    const cbColor = isP || isAdditional ? 'white' : isConflictFe ? 'red.600' : isLocked ? 'orange.700' : colors.textMuted

                    const availTag = () => {
                      if (isLocked)
                        return (
                          <Tag variant="lock">
                            🔒 ~{fe.lock_seconds_remaining ?? '?'}s
                          </Tag>
                        )
                      if (isConflictFe) return <Tag variant="red">Conflict</Tag>
                      if (fe.schedule_label) {
                        const partial = fe.schedule_label.toLowerCase().includes('from')
                        return (
                          <Tag variant={fe.available_at_schedule === false ? 'red' : partial ? 'amber' : 'green'}>
                            {fe.schedule_label}
                          </Tag>
                        )
                      }
                      if (fe.available_at_schedule === false) return <Tag variant="red">Unavailable</Tag>
                      return <Tag variant="green">Available</Tag>
                    }

                    return (
                      <Box
                        key={`${fe.field_engineer_id}-${uid ?? idx}`}
                        border="0.5px solid"
                        borderColor={cardBorder}
                        borderRadius="md"
                        p={2.5}
                        bg={cardBg}
                        opacity={dimmed ? 0.32 : 1}
                        transition="background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease"
                        _hover={
                          dimmed
                            ? undefined
                            : {
                                bg: hoverBg,
                                borderColor: hoverBorder,
                                boxShadow: 'sm',
                              }
                        }
                      >
                        <HStack align="flex-start" spacing={2}>
                          <Box
                            w="28px"
                            h="28px"
                            borderRadius="full"
                            bg={isP ? 'blue.100' : isAdditional ? 'green.100' : 'gray.100'}
                            color={isP ? 'blue.700' : isAdditional ? 'green.700' : 'gray.600'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="10px"
                            fontWeight="500"
                            flexShrink={0}
                          >
                            {getInitials(fe.full_name)}
                          </Box>
                          <Box flex={1} minW={0}>
                            <HStack flexWrap="wrap" gap={1} mb="2px">
                              <Text fontSize="12px" fontWeight="500">
                                {fe.full_name}
                              </Text>
                              {hasCertRequirement ? (
                                fe.has_required_certifications !== false ? (
                                  <Tag variant="blue">Cert ✓</Tag>
                                ) : (
                                  <Tag variant="red">No cert</Tag>
                                )
                              ) : null}
                              {availTag()}
                            </HStack>
                            <Text fontSize="10px" color={colors.textSecondary} mb={1}>
                              {fe.cluster ?? fe.launch_point ?? '—'} · {formatDistance(fe.distance_km, fe.distance_source)} ·{' '}
                              {formatTravelMinutes(fe.apt_travel_minutes)} ·{' '}
                              {fe.workload ?? '—'}/{fe.daily_capacity ?? '—'} tickets
                            </Text>
                            <HStack spacing={1} mb={1}>
                              <Text fontSize="9px" color={colors.textMuted} whiteSpace="nowrap">
                                8AM
                              </Text>
                              <HStack flex={1} spacing="1px" h="8px" borderRadius="4px" overflow="hidden">
                                {tl.map((v, i) => (
                                  <Box key={i} flex={1} h="100%" bg={timelineSegmentColor(v)} />
                                ))}
                              </HStack>
                              <Text fontSize="9px" color={colors.textMuted} whiteSpace="nowrap">
                                6PM
                              </Text>
                            </HStack>
                            <HStack spacing={2} align="center">
                              <HStack flex={1} h="4px" borderRadius="2px" overflow="hidden" spacing="1px">
                                <Box flex={fe.apt_travel_minutes ?? 30} bg={colors.brandBlue} />
                                <Box flex={fe.apt_repair_minutes ?? 90} bg="green.500" />
                              </HStack>
                              <Text fontSize="10px" fontWeight="500" whiteSpace="nowrap">
                                APT {formatAptMinutes(fe.apt_travel_minutes, fe.apt_repair_minutes)}
                              </Text>
                              <Box w="46px" h="4px" borderRadius="2px" bg="gray.100" overflow="hidden">
                                <Box
                                  w={`${Math.min(100, Math.round(fe.score))}%`}
                                  h="100%"
                                  bg={scoreBarColor(fe.score)}
                                />
                              </Box>
                              <Text fontSize="10px" color={colors.textMuted}>
                                {Math.round(fe.score)}
                              </Text>
                            </HStack>
                          </Box>
                          <Box
                            as="button"
                            w="30px"
                            h="30px"
                            borderRadius="full"
                            border="1.5px solid"
                            borderColor={isP ? colors.brandBlue : isAdditional ? 'green.600' : isConflictFe ? 'red.300' : isLocked ? 'orange.300' : 'gray.300'}
                            bg={cbBg}
                            color={cbColor}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="10px"
                            fontWeight="500"
                            flexShrink={0}
                            cursor={isLocked || isConflictFe || isPickBlocked ? 'default' : 'pointer'}
                            opacity={isPickBlocked ? 0.45 : 1}
                            onClick={() => {
                              if (isLocked || isConflictFe || isPickBlocked || !fe.user_id) return
                              handleManualSelect(fe)
                            }}
                          >
                            {cbLabel}
                          </Box>
                        </HStack>
                        {isLocked ? (
                          <Box
                            mt={1.5}
                            p={2}
                            border="0.5px solid"
                            borderColor="orange.200"
                            borderRadius="md"
                            bg={colors.cardBg}
                            fontSize="10px"
                            color="orange.700"
                          >
                            <HStack spacing={1} mb={1}>
                              <Lock size={11} />
                              <Text>
                                <Text as="strong">{fe.full_name}</Text> is being assigned to another ticket (~
                                {fe.lock_seconds_remaining ?? '?'}s).
                              </Text>
                            </HStack>
                            <HStack spacing={1}>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="orange"
                                fontSize="10px"
                                onClick={() => {
                                  if (!fe.user_id) return
                                  handleManualSelect(fe)
                                }}
                              >
                                Select anyway
                              </Button>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="orange"
                                fontSize="10px"
                                onClick={() => onRefresh?.()}
                              >
                                Refresh
                              </Button>
                            </HStack>
                          </Box>
                        ) : null}
                      </Box>
                    )
                  })}
                  {hasMoreManualListItems ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme={effectiveManualMode === 'additional' ? 'green' : 'blue'}
                      fontSize="12px"
                      mt={1}
                      isDisabled={isManualListBusy}
                      onClick={() =>
                        setManualListVisibleCount((prev) => prev + MANUAL_LIST_PAGE_SIZE)
                      }
                    >
                      Load more ({manualListRecommendations.length - manualListVisibleCount} remaining)
                    </Button>
                  ) : null}
                </VStack>
                </Box>

                {isManualListBusy ? (
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    bg="whiteAlpha.700"
                    borderRadius="md"
                    zIndex={1}
                  >
                    <HStack
                      spacing={2}
                      bg={colors.cardBg}
                      px={3}
                      py={2}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={colors.borderLight}
                      boxShadow="sm"
                    >
                      <Spinner
                        size="sm"
                        color={effectiveManualMode === 'additional' ? 'green.500' : 'blue.500'}
                        thickness="2px"
                      />
                      <Text fontSize="12px" color={colors.textMuted}>
                        {manualListBusyMessage}
                      </Text>
                    </HStack>
                  </Flex>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </VStack>
  )
}
